<script setup>
import { useAbility } from '@casl/vue'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { VDataTable } from 'vuetify/lib/labs/VDataTable/index.mjs'

import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CommitteeService from '@/services/quality/committee.service'
import QuorumRuleEditor from '@/views/quality/committee/QuorumRuleEditor.vue'
import StandardRefChips from '@/views/quality/committee/StandardRefChips.vue'
import { ReviewFrequency, reviewFrequencyTitle } from '@/enums/quality/review-frequency'

// unplugin-vue-router derives the route name from the file path
// (`app-quality-committee-masters-types`) — see
// src/pages/app/quality/risk/masters/types.vue for the same convention
// (meta.name is a CASL key, not a route name, so it is intentionally absent
// here). The subject below is the short form — a 4-part permission name
// drops its module segment when abilities are built, so the long
// `quality.committee` form would silently block the page.
definePage({
  meta: {
    subject: 'committee.menu',
    action: 'masters',
  },
})

const { can } = useAbility()
const unitId = cookieRef('selectedUnit')

// ── List ─────────────────────────────────────────────
// CommitteeTypeController::index has no pagination, search or sort params —
// it always returns the full collection visible to the caller
// (Modules/Quality/App/Http/Controllers/Committee/CommitteeTypeController.php).
// A VDataTableServer bound to page/per-page would therefore be decorative:
// the backend ignores those params and returns everything every time, so
// paging controls would do nothing. Plain VDataTable with its built-in
// client-side `search` prop matches what the endpoint actually provides.
const searchQuery = ref('')
const committeeTypes = ref([])
const isFetching = ref(false)

// Monotonic token guarding against out-of-order responses — same pattern as
// src/pages/app/quality/risk/masters/types.vue.
let fetchToken = 0

const fetchTypes = async () => {
  const token = ++fetchToken

  isFetching.value = true
  await CommitteeService.listTypes({}, data => {
    if (token !== fetchToken) return
    committeeTypes.value = data?.data ?? data ?? []
    isFetching.value = false
  }, () => {
    if (token !== fetchToken) return
    isFetching.value = false
  })
}

fetchTypes()

const headers = [
  { title: 'Code', key: 'code' },
  { title: 'Name', key: 'name' },
  { title: 'Default Frequency', key: 'default_frequency' },
  { title: 'Standard Refs', key: 'standard_refs', sortable: false },
  { title: 'Active', key: 'is_active', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, width: 100 },
]

const reviewFrequencyOptions = Object.values(ReviewFrequency).map(value => ({ title: reviewFrequencyTitle(value), value }))

// ── Drawer / Form ────────────────────────────────────
const isDrawerVisible = ref(false)
const refForm = ref()
const isSaving = ref(false)

const formErrors = ref({})

const blankForm = () => ({
  id: null,
  code: null,
  name: null,
  description: null,
  icon: null,
  default_frequency: null,
  default_quorum_rule: null,
  standard_refs: [],
  is_active: true,
  is_system: false,
})

const buildFormFromType = item => ({
  id: item.id,
  code: item.code ?? null,
  name: item.name ?? null,
  description: item.description ?? null,
  icon: item.icon ?? null,
  default_frequency: item.default_frequency ?? null,
  default_quorum_rule: item.default_quorum_rule ?? null,
  standard_refs: item.standard_refs ?? [],
  is_active: !!item.is_active,
  is_system: !!item.is_system,
})

const form = ref(blankForm())
const isEditing = computed(() => !!form.value.id)
const isSystemType = computed(() => !!form.value.is_system)
const drawerTitle = computed(() => (isEditing.value ? 'Edit Committee Type' : 'Add Committee Type'))

const openCreate = () => {
  form.value = blankForm()
  formErrors.value = {}
  isDrawerVisible.value = true
}

const openEdit = item => {
  form.value = buildFormFromType(item)
  formErrors.value = {}
  isDrawerVisible.value = true
}

const closeDrawer = () => {
  isDrawerVisible.value = false
  nextTick(() => {
    refForm.value?.resetValidation()
  })
}

const onFormFail = error => {
  isSaving.value = false
  formErrors.value = error?.data?.errors ?? {}
}

const submit = async () => {
  const { valid } = await refForm.value.validate()

  if (!valid) return

  const payload = {
    code: form.value.code,
    name: form.value.name,
    description: form.value.description,
    icon: form.value.icon,
    default_frequency: form.value.default_frequency,
    default_quorum_rule: form.value.default_quorum_rule,
    standard_refs: form.value.standard_refs,
    is_active: form.value.is_active,
    unit_id: unitId.value,
  }

  isSaving.value = true
  formErrors.value = {}

  const onDone = () => {
    isSaving.value = false
    closeDrawer()
    fetchTypes()
  }

  if (isEditing.value)
    await CommitteeService.updateType(form.value.id, payload, onDone, onFormFail)
  else
    await CommitteeService.createType(payload, onDone, onFormFail)
}

// ── Active toggle ────────────────────────────────────
// PUT is a full CommitteeTypeRequest validation, not a partial patch — every
// required field must be resent, not just is_active (see
// Modules/Quality/App/Http/Requests/Committee/CommitteeTypeRequest.php).
const togglingId = ref(null)

const toggleActive = async item => {
  togglingId.value = item.id

  const payload = {
    code: item.code,
    name: item.name,
    description: item.description,
    icon: item.icon,
    default_frequency: item.default_frequency,
    default_quorum_rule: item.default_quorum_rule,
    standard_refs: item.standard_refs,
    is_active: !item.is_active,
    unit_id: unitId.value,
  }

  await CommitteeService.updateType(item.id, payload, () => {
    togglingId.value = null
    fetchTypes()
  }, () => {
    togglingId.value = null
    fetchTypes() // resync the switch to server truth on failure
  })
}

// ── Delete ───────────────────────────────────────────
// System types cannot be deleted server-side
// (CommitteeTypeController::destroy aborts with 422) — the disabled button +
// tooltip below reflect that rather than offering an action that always
// fails.
const isConfirmDialogVisible = ref(false)
const deleteTarget = ref(null)

const showConfirmDialog = item => {
  deleteTarget.value = item
  isConfirmDialogVisible.value = true
}

const confirmDelete = async (confirm, data) => {
  if (confirm && data?.id) {
    await CommitteeService.deleteType(data.id, () => {
      isConfirmDialogVisible.value = false
      fetchTypes()
    })
  }
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <AppTextField
            v-model="searchQuery"
            placeholder="Search..."
            density="compact"
            style="inline-size: 280px;"
          >
            <template #prepend-inner>
              <VIcon
                icon="tabler-search"
                size="16"
              />
            </template>
          </AppTextField>
        </template>
        <template #append>
          <div class="d-flex align-center gap-3">
            <VBtn
              v-if="can('configure', 'quality.committee')"
              color="primary"
              variant="flat"
              prepend-icon="tabler-plus"
              @click="openCreate"
            >
              Add Committee Type
            </VBtn>

            <IconBtn
              variant="tonal"
              class="rounded"
              :loading="isFetching"
              @click="fetchTypes"
            >
              <VIcon icon="tabler-refresh" />
              <VTooltip activator="parent">
                Refresh
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VCardItem>

      <VDivider />

      <VDataTable
        :headers="headers"
        :items="committeeTypes"
        :search="searchQuery"
        :loading="isFetching"
        class="text-no-wrap"
      >
        <!-- Code -->
        <template #item.code="{ item }">
          <div class="d-flex align-center gap-1">
            <VIcon
              v-if="item.is_system"
              icon="tabler-lock"
              size="14"
              class="text-medium-emphasis"
            />
            <span class="font-weight-medium">{{ item.code }}</span>
          </div>
        </template>

        <!-- Default Frequency -->
        <template #item.default_frequency="{ item }">
          <span v-if="item.default_frequency">{{ reviewFrequencyTitle(item.default_frequency) }}</span>
          <span
            v-else
            class="text-disabled"
          >Not declared</span>
        </template>

        <!-- Standard Refs -->
        <template #item.standard_refs="{ item }">
          <StandardRefChips :codes="item.standard_refs ?? []" />
        </template>

        <!-- Active -->
        <template #item.is_active="{ item }">
          <VSwitch
            :model-value="item.is_active"
            :disabled="!can('configure', 'quality.committee') || togglingId === item.id"
            color="success"
            density="compact"
            hide-details
            @update:model-value="toggleActive(item)"
          />
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div
            v-if="can('configure', 'quality.committee')"
            class="d-flex justify-start gap-1"
          >
            <IconBtn
              variant="tonal"
              class="rounded"
              size="small"
              @click="openEdit(item)"
            >
              <VIcon icon="tabler-edit" />
              <VTooltip activator="parent">
                Edit
              </VTooltip>
            </IconBtn>

            <IconBtn
              v-if="!item.is_system"
              variant="tonal"
              class="rounded"
              size="small"
              color="error"
              @click="showConfirmDialog(item)"
            >
              <VIcon icon="tabler-trash" />
              <VTooltip activator="parent">
                Delete
              </VTooltip>
            </IconBtn>

            <IconBtn
              v-else
              variant="tonal"
              class="rounded"
              size="small"
              disabled
            >
              <VIcon icon="tabler-lock" />
              <VTooltip activator="parent">
                System types can be deactivated, not deleted.
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <!-- Add / Edit Drawer -->
    <VNavigationDrawer
      temporary
      :width="700"
      location="end"
      class="scrollable-content"
      :model-value="isDrawerVisible"
      @update:model-value="val => (isDrawerVisible = val)"
    >
      <AppDrawerHeaderSection
        :title="drawerTitle"
        @cancel="closeDrawer"
      />

      <PerfectScrollbar :options="{ wheelPropagation: false }">
        <VCard flat>
          <VCardText>
            <VAlert
              v-if="isSystemType"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              System type — Code and Standard Refs are the join key to verified
              accreditation references and cannot be edited. Deactivate instead
              of deleting.
            </VAlert>

            <VAlert
              type="info"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              Default frequency and quorum are suggestions a committee inherits.
              Neither NABH nor JCI states a committee meeting frequency or a
              quorum, so both ship blank on the seeded types.
            </VAlert>

            <VForm ref="refForm">
              <VRow>
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    v-model="form.code"
                    label="Code"
                    placeholder="COMMITTEE_CODE"
                    :disabled="isSystemType"
                    :error-messages="formErrors.code"
                    :rules="[requiredValidator, v => /^[A-Z0-9_]+$/.test(v || '') || 'Uppercase letters, numbers and underscores only']"
                    required
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    v-model="form.name"
                    label="Name"
                    placeholder="Enter committee type name"
                    :error-messages="formErrors.name"
                    :rules="[requiredValidator]"
                    required
                  />
                </VCol>

                <VCol cols="12">
                  <AppTextarea
                    v-model="form.description"
                    label="Description"
                    placeholder="Describe this committee type"
                    :is-speech-to-text="true"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <AppSelect
                    v-model="form.default_frequency"
                    label="Default Frequency"
                    placeholder="Select a suggested frequency"
                    :items="reviewFrequencyOptions"
                    item-title="title"
                    item-value="value"
                    clearable
                    :error-messages="formErrors.default_frequency"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    v-model="form.icon"
                    label="Icon"
                    placeholder="tabler-users-group"
                    hint="Tabler icon name"
                  />
                </VCol>

                <VCol cols="12">
                  <QuorumRuleEditor v-model="form.default_quorum_rule" />
                </VCol>

                <VCol cols="12">
                  <AppCombobox
                    v-model="form.standard_refs"
                    label="Standard Refs"
                    placeholder="Add a standard reference code and press Enter"
                    multiple
                    chips
                    closable-chips
                    :disabled="isSystemType"
                    :error-messages="formErrors.standard_refs"
                    hint="Codes must match an existing accreditation reference (e.g. COP.8.d, QPS.04.01 ME3)."
                    persistent-hint
                  />
                </VCol>

                <VCol cols="12">
                  <VSwitch
                    v-model="form.is_active"
                    label="Active"
                    color="success"
                  />
                </VCol>

                <VCol cols="12">
                  <VBtn
                    class="me-3"
                    :loading="isSaving"
                    @click="submit"
                  >
                    Submit
                  </VBtn>
                  <VBtn
                    variant="outlined"
                    color="secondary"
                    @click="closeDrawer"
                  >
                    Cancel
                  </VBtn>
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </VCard>
      </PerfectScrollbar>
    </VNavigationDrawer>

    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure you want to delete this committee type?"
      confirm-title="Deleted!"
      confirm-msg="Committee type deleted."
      :data="deleteTarget"
      @confirm="confirmDelete"
    />
  </section>
</template>
